import type { SignatureTemplate } from "@/shared/types";
import { getFromRoamingOrLocal, setRoamingSetting, removeFromStorage } from "@/shared/services/storage";
import { info } from "@/shared/services/logger";

const SOURCE = "profile-permissions";
const ROAMING_KEY = "helpitech_allow_editing";
const LOCAL_KEY = "allow_editing";

/**
 * Permissão de edição do perfil, definida pelo administrador na plataforma e
 * entregue no campo `allow_editing` da resposta de `/signatures/templates`.
 *
 * É persistida em cache (roaming settings + localStorage) porque a tela de
 * perfil pode ser aberta sem uma chamada nova à API — o valor cacheado vale até
 * a próxima resposta da plataforma.
 *
 * O padrão é **negar**: enquanto nada tiver sido sincronizado, os campos ficam
 * bloqueados. Isso mantém a permissão como uma liberação explícita do
 * administrador, e não algo que um cache ausente concede por acidente.
 *
 * @example
 * ```typescript
 * import { profilePermissions } from "@/shared/services/profile-permissions";
 *
 * profilePermissions.syncFromTemplates(templatesDaApi);
 * if (profilePermissions.allowEditing()) habilitaFormulario();
 * ```
 */
export const profilePermissions = {
  /**
   * Indica se o usuário pode editar os campos personalizáveis do perfil.
   *
   * @returns `true` somente se a plataforma tiver liberado a edição.
   *
   * @example
   * ```typescript
   * const podeEditar = profilePermissions.allowEditing();
   * ```
   */
  allowEditing(): boolean {
    return getFromRoamingOrLocal(ROAMING_KEY, LOCAL_KEY) === "true";
  },

  /**
   * Persiste a permissão nos dois armazenamentos.
   *
   * @param allowed - Valor vindo da plataforma.
   *
   * @example
   * ```typescript
   * profilePermissions.set(true);
   * ```
   */
  set(allowed: boolean): void {
    setRoamingSetting(ROAMING_KEY, String(allowed), LOCAL_KEY);
    info(SOURCE, `set: edição do perfil ${allowed ? "liberada" : "bloqueada"}.`);
  },

  /**
   * Extrai a permissão da resposta da API e atualiza o cache.
   *
   * Uma resposta **vazia** não carrega a informação (o backend só devolve
   * templates quando o destinatário tem grupo), então nesse caso o valor
   * cacheado é mantido em vez de ser rebaixado para `false`.
   *
   * @param templates - Templates retornados por `/signatures/templates`.
   *
   * @example
   * ```typescript
   * profilePermissions.syncFromTemplates(await fetchSignatureTemplates(email));
   * ```
   */
  syncFromTemplates(templates: readonly SignatureTemplate[]): void {
    if (!templates || templates.length === 0) return;

    this.set(templates.some((template) => template.allow_editing === true));
  },

  /**
   * Remove a permissão dos armazenamentos (usado no logout).
   *
   * @example
   * ```typescript
   * profilePermissions.clear();
   * ```
   */
  clear(): void {
    removeFromStorage(ROAMING_KEY, LOCAL_KEY);
  },
};
